import type { FC, ReactNode } from "react";
import { mergeClasses } from "@huub-web/utils";

import { Text } from "../../Text";
import { ResponseBody } from "../../ResponseBody";
import { useFormattedDate } from "../../../hooks/useFormattedDate";
import { Flex } from "../../Flex";
import type { ChatViewVariant } from "../ChatView";
import type { ChatMessage } from "../../../types/ChatConversation";
import type { ChildrenProp } from "../../../props/ChildrenProp";
import { DebugLabel } from "../../DebugLabel";

import { visualDebugContainerStyle } from "../../../theme/css/visualDebugContainer.css";
import type { ClassNameProp } from "../../../props";
import { Chip } from "../../Chip";
import { Icon } from "../../Icon";

type AgentMessageProps = ChildrenProp &
  ClassNameProp & {
    message: ChatMessage<ReactNode>;
    variant?: ChatViewVariant;
    showTime?: boolean;
    debug?: boolean;
  };

/**
 * Displays a message's time and content
 */
export const AgentMessage: FC<AgentMessageProps> = ({
  message,
  variant,
  showTime = true,
  debug = false,
  children,
  className,
}) => {
  const { formatDate } = useFormattedDate();

  const time = formatDate(message.created, { format: "minutesHours" });

  return (
    <Flex
      className={mergeClasses(
        debug ? visualDebugContainerStyle : undefined,
        className,
      )}
      direction="column"
      gap="s"
      data-id={message.id}
      data-component="AgentMessage"
      data-variant={variant}
      overflowHidden
      grow
    >
      {debug && (
        <DebugLabel
          trailing={
            message.context && (
              <Text variant="monospace">{message.context.join(", ")}</Text>
            )
          }
        >
          [{message.id}]
        </DebugLabel>
      )}
      {message.interaction && !!message.interactionLabel && (
        <Chip variant="tertiary" alignSelf="flex-start">
          {message.interactionLabel}
        </Chip>
      )}
      {showTime && (
        <Text variant="bodyXS" color="currentColor50" align="left">
          {time}
        </Text>
      )}
      <ResponseBody variant="sideContent" content={message.content} />
      {children}
      {message.verificationState === "unverified" && (
        <Chip variant="currentColor" alignSelf="flex-start">
          Ongeverifieerd antwoord
        </Chip>
      )}
      {message.verificationState === "verified" && (
        <Chip
          variant="currentColor"
          alignSelf="flex-start"
          leading={<Icon icon="shield" size="s" />}
        >
          Geverifieerd antwoord
        </Chip>
      )}
    </Flex>
  );
};
